/**
 * Video Playback Service
 *
 * Handles automatic video playback with retry logic.
 * Solves the "guest joins first → blank video" issue on iPhone Safari and Android Chrome.
 */

import { PlatformInfo } from '@/lib/platform';
import { PLATFORM_CONSTANTS, getPlatformTimeout } from '@/lib/platform/constraints';

/**
 * Video playback options
 */
export interface PlayVideoOptions {
  platform: PlatformInfo;
  isMuted?: boolean;
  retries?: number;
  forceReload?: boolean;
}

/**
 * Video Playback Service Class
 */
export class VideoPlaybackService {
  /**
   * Plays a video element with automatic retry logic
   * Critical for mobile Safari and Chrome autoplay policies
   */
  async playVideo(
    videoElement: HTMLVideoElement,
    stream: MediaStream,
    options: PlayVideoOptions
  ): Promise<void> {
    const { platform, isMuted = false, retries = PLATFORM_CONSTANTS.MAX_PLAYBACK_RETRIES, forceReload = false } = options;

    console.log('[VideoPlaybackService] Playing video', {
      platform: platform.type,
      isMuted,
      retries,
      forceReload,
    });

    try {
      // Step 1: Set video element properties
      this.configureVideoElement(videoElement, stream, isMuted, platform);

      // Step 2: Force reload if requested (critical for mobile)
      if (forceReload) {
        videoElement.load();
      }

      // Step 3: Wait for metadata to load
      await this.waitForMetadata(videoElement, platform);

      // Step 4: Attempt play with retry logic
      await this.attemptPlayWithRetry(videoElement, retries, platform);

      console.log('[VideoPlaybackService] Video playback started successfully');
    } catch (error) {
      console.error('[VideoPlaybackService] Failed to play video:', error);
      throw error;
    }
  }

  /**
   * Configures video element with proper attributes
   */
  private configureVideoElement(
    videoElement: HTMLVideoElement,
    stream: MediaStream,
    isMuted: boolean,
    platform: PlatformInfo
  ): void {
    // Set stream
    videoElement.srcObject = stream;

    // Mute if requested (helps with autoplay policies)
    videoElement.muted = isMuted;

    // iOS requires playsInline to prevent fullscreen takeover
    videoElement.playsInline = true;

    // Additional attributes for mobile
    if (platform.isMobile || platform.isWebView) {
      videoElement.setAttribute('playsinline', 'true');
      videoElement.setAttribute('webkit-playsinline', 'true');
    }

    // Disable controls (we have custom controls)
    videoElement.controls = false;

    // Prevent download option using controlsList attribute
    videoElement.setAttribute('controlsList', 'nodownload');
  }

  /**
   * Waits for video metadata to load
   */
  private async waitForMetadata(videoElement: HTMLVideoElement, platform: PlatformInfo): Promise<void> {
    // If metadata already loaded, return immediately
    if (videoElement.readyState >= videoElement.HAVE_METADATA) {
      return;
    }

    const timeout = getPlatformTimeout(platform, 'VIDEO_LOAD_TIMEOUT');

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout waiting for video metadata'));
      }, timeout);

      const handleLoadedMetadata = () => {
        cleanup();
        resolve();
      };

      const handleError = () => {
        cleanup();
        reject(new Error('Error loading video metadata'));
      };

      const cleanup = () => {
        clearTimeout(timeoutId);
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('error', handleError);
      };

      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
      videoElement.addEventListener('error', handleError, { once: true });
    });
  }

  /**
   * Attempts to play video with exponential backoff retry
   */
  private async attemptPlayWithRetry(
    videoElement: HTMLVideoElement,
    maxRetries: number,
    platform: PlatformInfo
  ): Promise<void> {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Attempt to play
        await videoElement.play();

        // Success!
        return;
      } catch (error: any) {
        const isLastAttempt = attempt === maxRetries - 1;

        // Check error type
        if (error.name === 'NotAllowedError') {
          console.warn(`[VideoPlaybackService] Autoplay blocked (attempt ${attempt + 1}/${maxRetries})`);

          if (isLastAttempt) {
            // On mobile, sometimes a small delay helps
            if (platform.isMobile || platform.isWebView) {
              console.log('[VideoPlaybackService] Trying one more time after brief delay...');
              await this.delay(PLATFORM_CONSTANTS.RETRY_DELAY_BASE * 2);
              try {
                await videoElement.play();
                return;
              } catch (finalError) {
                throw new Error('Autoplay blocked - user interaction required');
              }
            }

            throw new Error('Autoplay blocked - user interaction required');
          }

          // Wait before retry (exponential backoff)
          const delay = PLATFORM_CONSTANTS.RETRY_DELAY_BASE * Math.pow(2, attempt);
          await this.delay(delay);
        } else if (error.name === 'AbortError') {
          console.warn(`[VideoPlaybackService] Play aborted (attempt ${attempt + 1}/${maxRetries})`);

          if (isLastAttempt) {
            throw error;
          }

          // Small delay before retry
          await this.delay(PLATFORM_CONSTANTS.RETRY_DELAY_BASE);
        } else {
          // Other error - throw immediately
          throw error;
        }
      }
    }

    throw new Error('Failed to play video after maximum retries');
  }

  /**
   * Refreshes a video stream (complete reset)
   * Used when guest joins first and host needs to see the video
   */
  async refreshStream(
    videoElement: HTMLVideoElement,
    stream: MediaStream,
    platform: PlatformInfo
  ): Promise<void> {
    console.log('[VideoPlaybackService] Refreshing video stream');

    try {
      // Step 1: Pause current playback
      videoElement.pause();

      // Step 2: Clear srcObject
      videoElement.srcObject = null;

      // Step 3: Small delay to ensure cleanup
      await this.delay(PLATFORM_CONSTANTS.STREAM_REFRESH_DELAY);

      // Step 4: Set new stream and play
      await this.playVideo(videoElement, stream, {
        platform,
        isMuted: videoElement.muted,
        forceReload: true,
      });

      console.log('[VideoPlaybackService] Stream refreshed successfully');
    } catch (error) {
      console.error('[VideoPlaybackService] Failed to refresh stream:', error);
      throw error;
    }
  }

  /**
   * Plays video after user interaction (fallback for autoplay failures)
   */
  async playAfterInteraction(videoElement: HTMLVideoElement): Promise<void> {
    console.log('[VideoPlaybackService] Attempting play after user interaction');

    try {
      await videoElement.play();
      console.log('[VideoPlaybackService] Playback started after user interaction');
    } catch (error) {
      console.error('[VideoPlaybackService] Failed to play even after user interaction:', error);
      throw error;
    }
  }

  /**
   * Checks if video is playing
   */
  isPlaying(videoElement: HTMLVideoElement): boolean {
    return !videoElement.paused && !videoElement.ended && videoElement.readyState > videoElement.HAVE_CURRENT_DATA;
  }

  /**
   * Checks if autoplay is likely to work
   */
  canAutoplay(platform: PlatformInfo, isMuted: boolean): boolean {
    // iOS never allows unmuted autoplay
    if (platform.isIOS && !isMuted) {
      return false;
    }

    // Android WebView is restrictive
    if (platform.isAndroid && platform.isWebView && !isMuted) {
      return false;
    }

    // Muted autoplay generally works
    if (isMuted) {
      return true;
    }

    // Desktop browsers are more permissive
    if (platform.isDesktop) {
      return true;
    }

    // Conservative default
    return false;
  }

  /**
   * Ensures video element is visible and has minimum size
   * Some browsers won't autoplay tiny or hidden videos
   */
  ensurePlayableSize(videoElement: HTMLVideoElement): void {
    const style = window.getComputedStyle(videoElement);
    const width = parseInt(style.width, 10);
    const height = parseInt(style.height, 10);

    // Ensure minimum size (some browsers require this for autoplay)
    if (width < 1 || height < 1) {
      console.warn('[VideoPlaybackService] Video element too small, setting minimum size');
      videoElement.style.minWidth = '1px';
      videoElement.style.minHeight = '1px';
    }

    // Ensure not display:none
    if (style.display === 'none') {
      console.warn('[VideoPlaybackService] Video element hidden, making visible');
      videoElement.style.display = 'block';
    }
  }

  /**
   * Utility: Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
